import { useState, useRef, useCallback, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

export interface CatalogItem {
  id: string;
  make: string;
  model: string;
  version: string | null;
  yearFrom: number;
  yearTo: number | null;
  engineSize: string | null;
  fuelType: string | null;
  transmission: string | null;
  bodyType: string | null;
  doors: number | null;
  source: string;
}

interface Props {
  value: string;
  onChangeText: (text: string) => void;
  onSelect: (item: CatalogItem) => void;
  year?: number;
  placeholder?: string;
}

function formatYearRange(from: number, to: number | null) {
  if (!to || to === from) return String(from);
  return `${from}-${to}`;
}

export function CatalogAutocomplete({
  value,
  onChangeText,
  onSelect,
  year,
  placeholder = 'Ex: Gol, Civic, Onix...',
}: Props) {
  const [results, setResults] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searched, setSearched] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = useCallback(
    async (q: string) => {
      if (q.length < 2) {
        setResults([]);
        setShowDropdown(false);
        setSearched(false);
        return;
      }

      setLoading(true);
      setSearched(true);

      try {
        const params: Record<string, string | number> = { q };
        if (year) params['year'] = year;
        const { data } = await api.get('/vehicle-catalog/search', { params });
        setResults(data.results ?? []);
        setShowDropdown(true);
      } catch {
        setResults([]);
        setShowDropdown(true);
      } finally {
        setLoading(false);
      }
    },
    [year],
  );

  const handleChange = useCallback(
    (text: string) => {
      onChangeText(text);

      if (timerRef.current) clearTimeout(timerRef.current);

      if (text.length < 2) {
        setResults([]);
        setShowDropdown(false);
        setSearched(false);
        return;
      }

      timerRef.current = setTimeout(() => {
        search(text);
      }, 300);
    },
    [onChangeText, search],
  );

  const handleSelect = useCallback(
    (item: CatalogItem) => {
      setShowDropdown(false);
      setSearched(false);
      onSelect(item);
    },
    [onSelect],
  );

  return (
    <div ref={wrapperRef} className="relative z-10">
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => {
            if (results.length > 0) setShowDropdown(true);
          }}
          placeholder={placeholder}
          className="block h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
        />
        {loading && (
          <Loader2
            size={16}
            className="absolute right-3 top-3 animate-spin text-muted-foreground"
          />
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-md">
          {results.length > 0 ? (
            results.map((item) => (
              <button
                key={item.id}
                type="button"
                className="flex w-full flex-col px-3 py-2.5 text-left hover:bg-muted/60"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(item)}
              >
                <span className="text-sm font-semibold">
                  {item.make} {item.model}
                </span>
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  {item.version && (
                    <span className="max-w-[180px] truncate">{item.version}</span>
                  )}
                  <span className="font-medium text-foreground/70">
                    {formatYearRange(item.yearFrom, item.yearTo)}
                  </span>
                  {item.fuelType && (
                    <span className="capitalize">{item.fuelType}</span>
                  )}
                </span>
              </button>
            ))
          ) : searched && !loading ? (
            // Sugestão de dropdown: fica fora do EmptyState do kit de
            // propósito. Já diz o próximo passo ("continue preenchendo
            // manualmente"), e um bloco com ícone dentro do dropdown cobriria
            // o campo que a pessoa está digitando.
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-muted-foreground">
                Nenhum veículo encontrado no catálogo.
              </p>
              <p className="mt-1 text-xs text-muted-foreground/70">
                Continue preenchendo manualmente.
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
